import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import { JuiIcon } from '@jump-tech-frontend/core-components-angular';
import { DEFAULT_RESIZE, ResizeOptions } from '../model';
import { DEFAULT_TORCH_CAPTURE_COPY, TorchCaptureCopy, TorchUnavailableReason } from './torch-capture-copy';
import { TorchCapturePresenter } from './torch-capture.presenter';
import { TorchCameraService } from './torch-camera.service';
import { PinchPoint, PinchTracker } from './pinch-tracker';

const pinchPoint = (event: PointerEvent): PinchPoint => ({ id: event.pointerId, x: event.clientX, y: event.clientY });

@Component({
  selector: 'media-capture-torch',
  templateUrl: './torch-capture.component.html',
  styleUrls: ['./torch-capture.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TorchCapturePresenter, TorchCameraService],
  imports: [JuiIcon]
})
export class TorchCaptureComponent implements AfterViewInit {
  readonly copy = input<TorchCaptureCopy>(DEFAULT_TORCH_CAPTURE_COPY);
  readonly resize = input<ResizeOptions>(DEFAULT_RESIZE);

  readonly captured = output<string>();
  readonly cancelled = output<void>();
  readonly unavailable = output<TorchUnavailableReason>();

  protected readonly presenter = inject(TorchCapturePresenter);
  private readonly preview = viewChild.required<ElementRef<HTMLVideoElement>>('preview');
  private readonly pinch = new PinchTracker();

  constructor() {
    this.presenter.load({
      copy: this.copy,
      resize: this.resize,
      outputs: { captured: this.captured, cancelled: this.cancelled, unavailable: this.unavailable }
    });
  }

  public ngAfterViewInit(): void {
    const video = this.preview().nativeElement;
    queueMicrotask(() => void this.presenter.open(video));
  }

  public onPointerDown(event: PointerEvent): void {
    if (this.pinch.down(pinchPoint(event))?.kind === 'start') {
      this.presenter.startPinch();
    }
  }

  public onPointerMove(event: PointerEvent): void {
    const pinch = this.pinch.move(pinchPoint(event));
    if (pinch?.kind === 'scale') {
      this.presenter.pinchTo(pinch.scale);
    }
  }

  public onPointerUp(event: PointerEvent): void {
    this.pinch.up(event.pointerId);
  }
}
